import { ERA_LOOKS, SERVES } from '@continuum/show-core';
import type { Serve as ServeData } from '@continuum/show-core';
import { Screen } from '../ui/Shell.js';
import { mono } from '../ui/atoms.js';
import type { Copy } from '../ui/text.js';
import type { ScreenCopy } from '../ui/screenText.js';

const SERVE_BG: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: 'linear-gradient(180deg,#060b18,#0a1122)',
  2: 'linear-gradient(180deg,#0d0618,#150a24)',
  3: 'linear-gradient(180deg,#120305,#1c0508)',
  4: 'linear-gradient(180deg,#140d05,#211609)',
  5: 'linear-gradient(180deg,#eae4d0,#e4dec9)',
};

const NOTE_COLOR: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: '#8fa6c4',
  2: '#b795cf',
  3: '#d49a94',
  4: '#bd9c72',
  5: '#5f6d55',
};

const PRODUCT_COLOR: Record<1 | 2 | 3 | 4 | 5, string> = {
  1: '#9dc4ee',
  2: '#e0aaff',
  3: '#ffb3a3',
  4: '#dbb27e',
  5: '#5c7d4e',
};

/**
 * The drink for the era: one per era, five in the evening. The drink's own words are the menu's: its
 * name, what is in it and its line, and nothing more.
 */
export function Serve({ chapter, s }: { chapter: 1 | 2 | 3 | 4 | 5; t: Copy; s: ScreenCopy }) {
  const serve: ServeData = SERVES[chapter];
  const look = ERA_LOOKS[chapter];

  return (
    <Screen style={{ background: SERVE_BG[chapter], color: look.fg, padding: 0 }}>
      {chapter === 3 && (
        <div
          style={{
            flex: 'none',
            background: 'repeating-linear-gradient(45deg,#ff2e3f 0 14px,#2a070a 14px 28px)',
            padding: 7,
            textAlign: 'center',
            fontFamily: mono,
            fontSize: 11,
            letterSpacing: 3,
            color: '#fff',
          }}
        >
          {s.redAlert}
        </div>
      )}

      <EraArt chapter={chapter} title={s.eraTitle(chapter)} />

      <div style={{ padding: '20px 24px 26px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <div
          style={{
            fontFamily: mono,
            fontSize: 11,
            letterSpacing: 2,
            color: chapter === 2 ? '#3ff2e6' : look.acc,
          }}
        >
          {s.drinkOf(serve.index)}
        </div>
        <h1
          style={{
            margin: '8px 0 2px',
            fontSize: 34,
            fontWeight: 700,
            letterSpacing: 3,
            textShadow: chapter === 2 ? '0 0 24px rgba(255,79,216,.45)' : undefined,
          }}
        >
          {serve.name}
        </h1>
        <div
          style={{ fontSize: 15, fontWeight: 600, letterSpacing: 1, color: PRODUCT_COLOR[chapter] }}
        >
          {serve.ingredients}
        </div>
        <p
          style={{
            margin: '14px 0 0',
            fontFamily: mono,
            fontSize: 13,
            lineHeight: 1.6,
            color: NOTE_COLOR[chapter],
            textWrap: 'pretty',
          }}
        >
          {serve.note}
        </p>
      </div>
    </Screen>
  );
}

/** The era illustration above each serve, recreated from the prototype. */
function EraArt({ chapter, title }: { chapter: 1 | 2 | 3 | 4 | 5; title: string }) {
  const look = ERA_LOOKS[chapter];
  const serve = SERVES[chapter];
  const height = chapter === 3 ? 180 : 216;

  return (
    <div style={{ position: 'relative', height, flex: 'none', overflow: 'hidden' }}>
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 44,
          right: 16,
          fontFamily: mono,
          fontSize: 64,
          fontWeight: 600,
          color: `${look.acc}1a`,
          letterSpacing: 4,
        }}
      >
        {serve.era}
      </div>

      {chapter === 1 && (
        <>
          <div
            style={{
              position: 'absolute',
              bottom: -170,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 340,
              height: 340,
              borderRadius: '50%',
              background: 'radial-gradient(circle at 50% 18%,#3d7fd6 0%,#173a6e 45%,#0b1c3c 75%)',
              boxShadow: '0 -18px 60px -10px rgba(111,183,255,.55)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: -172,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 352,
              height: 352,
              borderRadius: '50%',
              border: '1px solid rgba(140,200,255,.5)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: 56,
              left: 34,
              width: 26,
              height: 3,
              borderRadius: 2,
              background: 'rgba(255,255,255,.35)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: 78,
              left: 70,
              width: 16,
              height: 3,
              borderRadius: 2,
              background: 'rgba(255,255,255,.22)',
            }}
          />
        </>
      )}

      {chapter === 2 && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 150,
            display: 'flex',
            alignItems: 'flex-end',
            gap: 7,
            padding: '0 24px',
          }}
        >
          {[
            ['52%', '#ff4fd8', '#5b1b74', 'rgba(255,79,216,.45)'],
            ['88%', '#3ff2e6', '#123b57', 'rgba(63,242,230,.4)'],
            ['38%', '#b44fff', '#33124e', ''],
            ['70%', '#ff4fd8', '#4a1560', 'rgba(255,79,216,.35)'],
            ['100%', '#3ff2e6', '#0e2f46', 'rgba(63,242,230,.5)'],
            ['60%', '#b44fff', '#2b0f42', ''],
          ].map(([h, top, bottom, glow], i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: h,
                background: `linear-gradient(180deg,${top},${bottom})`,
                boxShadow: glow ? `0 0 22px ${glow}` : undefined,
                borderRadius: '2px 2px 0 0',
              }}
            />
          ))}
        </div>
      )}

      {chapter === 3 && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(ellipse at 50% 120%,rgba(255,46,63,.35),transparent 70%)',
            animation: 'ccGlitch 4s infinite',
          }}
        />
      )}

      {chapter === 4 && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 130,
            background:
              'linear-gradient(180deg,transparent,rgba(224,138,60,.25)), repeating-linear-gradient(85deg,rgba(224,138,60,.10) 0 3px,transparent 3px 9px)',
          }}
        />
      )}

      {chapter === 5 && (
        <div
          style={{
            position: 'absolute',
            bottom: -60,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 220,
            height: 220,
            borderRadius: '50%',
            border: '1px solid rgba(92,125,78,.5)',
            background: 'conic-gradient(from 200deg,#5c7d4e,#a8c48a,#e4dec9,#5c7d4e)',
            opacity: 0.5,
            animation: 'ccSwirl 60s linear infinite',
          }}
        />
      )}

      <div
        style={{
          position: 'absolute',
          top: 18,
          left: 16,
          right: 16,
          fontFamily: mono,
          fontSize: 11,
          letterSpacing: 2,
          color: look.acc,
          textShadow: '0 1px 4px rgba(0,0,0,.6)',
        }}
      >
        {title}
      </div>
    </div>
  );
}
